/**
 * Le catalogue des fournisseurs et des modèles (bloc `llm`, tc_embed_be
 * domain/llm_catalog.go) : logique pure. Les règles par rôle sont celles du
 * serveur (`validateLevel`) ; l'écran les montre avant l'enregistrement, le
 * serveur les impose.
 */
import type {
  AudioPrice,
  CatalogModel,
  LlmModelCaps,
  LlmProvider,
  LlmProviderKind,
  LlmRole,
  LlmSettings,
  ReasoningEffort,
} from "../../models/domain";

/** `<fournisseur>/<modèle>` : la clé de `llm.models`, des tarifs et des estimations. */
export const modelKey = (provider: string, model: string) => `${provider}/${model}`;

export const PROVIDER_KINDS: { kind: LlmProviderKind; label: string }[] = [
  { kind: "openai", label: "OpenAI" },
  { kind: "openai_compatible", label: "Compatible OpenAI" },
  { kind: "anthropic", label: "Anthropic" },
];

export const kindLabel = (kind: string) => PROVIDER_KINDS.find((k) => k.kind === kind)?.label ?? kind;

export const REASONING_EFFORTS: ReasoningEffort[] = ["none", "minimal", "low", "medium", "high"];

/** Identifiant de fournisseur : la règle du serveur et de tc_ecosys 0132. */
export const validProviderId = (id: string) => /^[a-z0-9][a-z0-9_-]{0,39}$/.test(id);

/**
 * Slug d'hébergeur d'un agrégateur (« anthropic », « google-vertex/us-east5 ») :
 * la règle de structure du serveur (`validRoutingSlug`).
 */
export const validRoutingSlug = (s: string) => /^[a-z0-9._-]+(\/[a-z0-9._-]+)*$/.test(s) && s.length <= 80;

/** Chemin du test de clé (« /key ») : la règle du serveur (`ValidKeyCheckPath`). */
export const validKeyCheckPath = (p: string) =>
  /^(\/[a-z0-9._-]+)+$/.test(p) && !p.includes("..") && p.length <= 80;

/** La liste d'hébergeurs saisie (séparée par des virgules ou des espaces), sans vides. */
export const parseRoutingOrder = (text: string): string[] =>
  text
    .split(/[\s,]+/)
    .map((h) => h.trim())
    .filter(Boolean);

/** Pourquoi cette liste d'hébergeurs est refusée ; null = elle convient. */
export const routingOrderProblem = (order: string[]): string | null => {
  if (order.length === 0) return "au moins un hébergeur";
  if (order.length > 8) return "8 hébergeurs au plus";
  const bad = order.find((h) => !validRoutingSlug(h));
  if (bad) return `« ${bad} » : minuscules, chiffres, - _ . et / pour une variante`;
  if (new Set(order).size !== order.length) return "hébergeur en double";
  return null;
};

/** Le routage en clair, pour la liste des modèles (« épinglé : anthropic »). */
export const routingLabel = (r: LlmModelCaps["routing"]): string | null =>
  r ? `${r.allow_fallbacks ? "préféré" : "épinglé"} : ${r.order.join(" › ")}` : null;

/** Les fournisseurs actifs, triés par libellé : ceux qu'un rôle peut choisir. */
export const activeProviders = (llm: Pick<LlmSettings, "providers">): { id: string; provider: LlmProvider }[] =>
  Object.entries(llm.providers ?? {})
    .filter(([, p]) => p.enabled)
    .map(([id, provider]) => ({ id, provider }))
    .sort((a, b) => a.provider.label.localeCompare(b.provider.label, "fr"));

/**
 * La route de la passerelle par laquelle un modèle s'appelle : le chat, ou
 * l'une des routes dédiées (OCR, synthèse vocale, transcription). Un modèle
 * d'une route dédiée ne fait que ça (le serveur impose qu'elles s'excluent).
 */
export type ModelKind = "chat" | "ocr_endpoint" | "tts" | "stt";

export const modelKind = (caps: LlmModelCaps): ModelKind =>
  caps.tts ? "tts" : caps.stt ? "stt" : caps.ocr_endpoint ? "ocr_endpoint" : "chat";

/**
 * Les routes qu'un rôle sait appeler : la voix n'appelle que la sienne, l'OCR
 * le chat (modèle qui lit les images) ou l'OCR dédié, tous les autres le chat.
 */
const ROLE_KINDS: Record<LlmRole, readonly ModelKind[]> = {
  objective_extraction: ["chat"],
  fact_extraction: ["chat"],
  fact_review: ["chat"],
  quest_generation: ["chat"],
  quest_review: ["chat"],
  course_writing: ["chat"],
  companion: ["chat"],
  ocr: ["chat", "ocr_endpoint"],
  tts: ["tts"],
  stt: ["stt"],
};

/**
 * Les modèles déclarés d'un fournisseur (suggestions du champ « Modèle »).
 * Avec un rôle, seulement ceux que ce rôle sait appeler : un modèle de
 * synthèse vocale n'est jamais proposé au générateur, ni un modèle de chat à
 * la lecture à voix haute.
 */
export const declaredModels = (llm: Pick<LlmSettings, "models">, provider: string, role?: LlmRole): string[] =>
  Object.entries(llm.models ?? {})
    .filter(([k, caps]) => k.startsWith(`${provider}/`) && (!role || ROLE_KINDS[role].includes(modelKind(caps))))
    .map(([k]) => k.slice(provider.length + 1))
    .sort();

/**
 * Les noms de modèles de chat déclarés chez un fournisseur actif, sans doublon
 * (essais d'extraction : le fournisseur est hérité, et seuls les rôles de chat
 * s'y essaient).
 */
export const declaredModelNames = (llm: Pick<LlmSettings, "providers" | "models">): string[] => [
  ...new Set(activeProviders(llm).flatMap(({ id }) => declaredModels(llm, id, "fact_extraction"))),
];

/**
 * Les replis possibles de la voix : les autres modèles de synthèse déclarés
 * chez le même fournisseur (le serveur exige le même fournisseur que le rôle,
 * et un modèle différent du principal).
 */
export const fallbackCandidates = (llm: Pick<LlmSettings, "models">, provider: string, model: string): string[] =>
  provider ? declaredModels(llm, provider, "tts").filter((m) => m !== model) : [];

/**
 * Pourquoi ce repli ne convient pas, en une phrase ; null = il convient (ou
 * il n'y en a pas). Mêmes règles de structure que le serveur.
 */
export const fallbackProblem = (
  llm: Pick<LlmSettings, "providers" | "models">,
  provider: string,
  model: string,
  fallback: string,
): string | null => {
  if (!fallback) return null;
  if (fallback === model) return "le repli doit être un autre modèle que le principal";
  if (!fallbackCandidates(llm, provider, model).includes(fallback))
    return `le repli doit être un modèle de synthèse vocale déclaré chez ${llm.providers?.[provider]?.label ?? provider}`;
  return null;
};

/**
 * Le rôle `tts` passe-t-il (même seulement en repli) par un modèle temps
 * réel ? Alors la passerelle ne rend que mp3 ou wav, faute de savoir encoder
 * le son brut dans un autre format.
 */
export const usesRealtime = (
  llm: Pick<LlmSettings, "providers" | "models">,
  provider: string,
  model: string,
  fallback: string,
): boolean => [model, fallback].some((m) => Boolean(m && capsOf(llm, provider, m)?.realtime));

export const capsOf = (
  llm: Pick<LlmSettings, "models">,
  provider: string | undefined,
  model: string | undefined,
): LlmModelCaps | undefined => (provider && model ? llm.models?.[modelKey(provider, model.trim())] : undefined);

/**
 * Ce que chaque rôle demande à un modèle de chat, en clair (règle `needs` du
 * serveur). Un modèle d'OCR dédié ou audio n'est pas un modèle de chat : il
 * suit la règle à part de `roleProblems`, et les rôles de la voix n'ont que
 * des modèles audio (aucune capacité de chat à exiger).
 */
const ROLE_NEEDS: Record<Exclude<LlmRole, "tts" | "stt">, { caps: (keyof LlmModelCaps)[]; studentData?: boolean; sendsTemperature: boolean }> = {
  objective_extraction: { caps: ["strict_schema"], sendsTemperature: true },
  fact_extraction: { caps: ["json_mode"], sendsTemperature: true },
  fact_review: { caps: ["json_mode"], sendsTemperature: true },
  quest_generation: { caps: ["strict_schema", "streaming"], sendsTemperature: true },
  quest_review: { caps: ["json_mode"], sendsTemperature: false },
  course_writing: { caps: ["json_mode"], sendsTemperature: true },
  companion: { caps: ["streaming"], studentData: true, sendsTemperature: true },
  ocr: { caps: ["vision"], sendsTemperature: true },
};

/** Les capacités d'un modèle de chat (cases du formulaire, pastilles de la liste). */
export const CHAT_CAPS = ["strict_schema", "json_mode", "streaming", "temperature", "reasoning", "vision"] as const;
export type ChatCap = (typeof CHAT_CAPS)[number];

export const CAP_LABELS: Record<ChatCap | "ocr_endpoint" | "tts" | "stt" | "realtime", string> = {
  strict_schema: "JSON strict",
  json_mode: "mode JSON",
  streaming: "flux",
  temperature: "température",
  reasoning: "raisonnement",
  vision: "images",
  ocr_endpoint: "OCR dédié",
  tts: "synthèse vocale",
  stt: "transcription",
  realtime: "temps réel",
};

/** Ce qu'un rôle de la voix demande, et ce qu'on dit d'un modèle audio posé ailleurs. */
const AUDIO_ROLE_NEED: Record<"tts" | "stt", string> = {
  tts: "ce rôle demande un modèle de synthèse vocale (déclaré tts)",
  stt: "ce rôle demande un modèle de transcription (déclaré stt)",
};
const AUDIO_ONLY: Record<"tts" | "stt", string> = {
  tts: "modèle de synthèse vocale : il ne fait pas de chat, il ne sert que la lecture à voix haute",
  stt: "modèle de transcription : il ne fait pas de chat, il ne sert que la dictée",
};

/**
 * La température se règle-t-elle pour ce modèle ? Jamais pour un modèle
 * d'une route dédiée (OCR, synthèse, transcription) : la passerelle ne lui en
 * envoie pas (il ne passe pas par le chat), même si ses capacités en
 * portaient une par erreur.
 */
export const acceptsTemperature = (caps: LlmModelCaps | undefined): boolean =>
  Boolean(caps && caps.temperature && modelKind(caps) === "chat");

/**
 * Pourquoi ce couple ne convient pas à ce rôle, en phrases ; vide = il
 * convient. `undefined` pour un modèle non déclaré : l'écran propose alors de
 * le déclarer.
 */
export const roleProblems = (
  llm: Pick<LlmSettings, "providers" | "models">,
  role: LlmRole,
  provider: string | undefined,
  model: string | undefined,
): string[] | undefined => {
  const p = provider ? llm.providers?.[provider] : undefined;
  if (!provider || !p) return [`fournisseur ${provider ? `« ${provider} » inconnu` : "à choisir"}`];
  const caps = capsOf(llm, provider, model);
  if (!caps) return model?.trim() ? undefined : ["modèle à choisir"];
  const out: string[] = [];
  if (!p.enabled) out.push(`${p.label} est désactivé`);
  // La voix : un modèle audio ne sert que son rôle, et un rôle de la voix ne
  // prend qu'un modèle de sa route. Les deux reçoivent des données d'élèves
  // (sa voix part à la transcription ; la lecture dit son prénom).
  const kind = modelKind(caps);
  if (role === "tts" || role === "stt") {
    if (kind !== role) out.push(kind === "tts" || kind === "stt" ? AUDIO_ONLY[kind] : AUDIO_ROLE_NEED[role]);
    if (!p.student_data_allowed) out.push(`${p.label} n'est pas autorisé à recevoir des données d'élèves`);
    return out;
  }
  if (kind === "tts" || kind === "stt") {
    out.push(AUDIO_ONLY[kind]);
    return out;
  }
  // Un modèle d'OCR dédié ne s'appelle que par `/v1/ocr` : il convient au
  // rôle `ocr` sans autre condition (ni images ni température, il n'en reçoit
  // pas) et à aucun autre, qui tous passent par le chat.
  if (caps.ocr_endpoint) {
    if (role !== "ocr") out.push("modèle d'OCR dédié : il ne fait pas de chat, il ne sert que la lecture des pages scannées");
    return out;
  }
  const needs = ROLE_NEEDS[role];
  for (const c of needs.caps) {
    if (!caps[c]) out.push(`ce rôle demande ${CAP_LABELS[c as ChatCap]}`);
  }
  if (needs.studentData && !p.student_data_allowed) out.push(`${p.label} n'est pas autorisé à recevoir des messages d'élèves`);
  if (needs.sendsTemperature && !caps.temperature) out.push("ce rôle envoie toujours une température, ce modèle la refuse");
  return out;
};

/** Le catalogue rendu par le serveur, réduit aux capacités (forme de `llm.models`). */
export const capsOnly = (models: Record<string, CatalogModel>): Record<string, LlmModelCaps> =>
  Object.fromEntries(
    Object.entries(models).map(([k, m]) => {
      const { provider: _p, model: _m, price: _price, ocr_price: _ocr, audio_price: _audio, ...caps } = m;
      return [k, caps];
    }),
  );

const usd = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 4 });

/** Un prix audio posé (le serveur rend 0 ou rien pour un champ inutilisé). */
const posed = (v: number | undefined): v is number => typeof v === "number" && v > 0;

/**
 * Le tarif d'un modèle audio : à la minute, aux caractères, ou aux jetons
 * (entrée et sortie, en dollars par million) ; null = aucun tarif lisible.
 */
const audioPriceLabel = (kind: "tts" | "stt", a: AudioPrice | null | undefined): string | null => {
  if (!a) return null;
  if (kind === "stt" && posed(a.per_audio_minute)) return `${usd.format(a.per_audio_minute)} $ / min`;
  if (kind === "tts" && posed(a.per_1m_characters)) return `${usd.format(a.per_1m_characters)} $ / M caractères`;
  if (posed(a.input_per_1m) && posed(a.output_per_1m)) {
    // Le texte produit par un modèle temps réel a son propre prix : sans lui,
    // la ligne laisserait croire que tous ses jetons de sortie coûtent l'audio.
    const text = kind === "tts" && posed(a.text_output_per_1m) ? ` · sortie texte ${usd.format(a.text_output_per_1m)}` : "";
    return `entrée ${usd.format(a.input_per_1m)} · sortie ${usd.format(a.output_per_1m)}${text} $ / M jetons`;
  }
  return null;
};

/**
 * Le tarif en vigueur d'un modèle, en clair ; null = aucun tarif lisible (le
 * modèle ne peut servir aucun rôle). Un modèle d'OCR dédié se paie à la page,
 * un modèle audio selon son tarif audio : on n'affiche que ce prix-là, le
 * serveur ignorant tout tarif de chat pour eux.
 */
export const priceLabel = (
  m: Pick<CatalogModel, "ocr_endpoint" | "tts" | "stt" | "price" | "ocr_price" | "audio_price">,
): string | null => {
  if (m.tts) return audioPriceLabel("tts", m.audio_price);
  if (m.stt) return audioPriceLabel("stt", m.audio_price);
  if (m.ocr_endpoint) return m.ocr_price ? `${usd.format(m.ocr_price.per_1k_pages)} $ / 1 000 pages` : null;
  if (!m.price) return null;
  const p = m.price;
  const base = `${usd.format(p.input_per_1m)} · ${usd.format(p.cached_input_per_1m)} · ${usd.format(p.output_per_1m)}`;
  return p.cache_write_per_1m !== undefined && p.cache_write_per_1m !== null
    ? `${base} · écriture cache ${usd.format(p.cache_write_per_1m)}`
    : base;
};

/** Capacités d'un modèle de chat courant, point de départ du formulaire « Déclarer ». */
export const DEFAULT_CAPS: LlmModelCaps = {
  strict_schema: true,
  json_mode: true,
  streaming: true,
  temperature: true,
  reasoning: false,
  reasoning_headroom: 0,
  max_output: 16384,
  vision: false,
};
